/**
 * Lock file I/O and integrity verification (schema v3).
 *
 * The lock file (skaile.lock.yaml) captures the complete resolved state: every
 * asset keyed by its canonical ref `<kind>:@<publisher>/<name>#<version>`, the
 * upstream source + commit it resolved from, a per-file + composite content
 * hash for drift detection, and whether an `overrides[]` entry pinned it. A
 * top-level `sources:` block records every contributing source URL at its
 * resolved commit.
 *
 * Old v1 lockfiles (`lockfile_version: 1`, `repositories:`) are rejected with a
 * clear regenerate-from-scratch error.
 */

import { createHash } from "node:crypto";
import { existsSync, readFileSync, writeFileSync } from "node:fs";
import { join } from "node:path";
import { parse, stringify } from "yaml";
import type { LockEntry, LockFile, LockPluginEntry, LockSourceEntry } from "./models.js";
import type { ProvenanceCandidate } from "./walker.js";

// ── Lock file I/O ────────────────────────────────────────────────────────────

/**
 * Serialize a `LockFile` to YAML and write it to disk.
 *
 * @param lockPath - Absolute path to write the lock file (e.g. `skaile.lock.yaml`)
 * @param data - Lock file data to serialize
 * @docLink packages/core/api-reference#write-lock
 */
export function writeLock(lockPath: string, data: LockFile): void {
  writeFileSync(lockPath, stringify(data, { lineWidth: 120 }));
}

/**
 * Read and parse a lock file from disk.
 *
 * @param lockPath - Absolute path to the lock file
 * @returns Parsed `LockFile`, or `null` if the file is missing or unparseable
 * @throws When the file is a legacy v1 lock — the shape changed and it must be regenerated.
 * @docLink packages/core/api-reference#read-lock
 */
export function readLock(lockPath: string): LockFile | null {
  if (!existsSync(lockPath)) return null;
  let raw: unknown;
  try {
    raw = parse(readFileSync(lockPath, "utf8"));
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (r.schema_version !== 3) {
    throw new Error(
      "outdated lockfile schema detected — the lockfile shape changed. " +
        "Regenerate: `rm skaile.lock.yaml && skaile install`.",
    );
  }
  return r as unknown as LockFile;
}

// ── Hashing ──────────────────────────────────────────────────────────────────

/**
 * Compute the SHA-256 hash of a file's content (hex digest, no prefix).
 *
 * @param filePath - Absolute path to the file
 * @returns Hex-encoded SHA-256 hash string
 * @docLink packages/core/api-reference#compute-hash
 */
export function computeHash(filePath: string): string {
  const content = readFileSync(filePath);
  return createHash("sha256").update(content).digest("hex");
}

/**
 * Composite hash: SHA-256 (bare 64-hex) over `localeCompare`-sorted
 * `<path>:<sha256>\n` rows. Matches the walker's `compositeSha256`, the store's
 * `computeCompositeSha256`, and the `InstallManifest.sha256` rollup.
 */
function compositeHash(files: Array<{ path: string; sha256: string }>): string {
  const lines = files
    .slice()
    .sort((a, b) => a.path.localeCompare(b.path))
    .map((f) => `${f.path}:${f.sha256}\n`)
    .join("");
  return createHash("sha256").update(lines).digest("hex");
}

// ── Lock file construction ───────────────────────────────────────────────────

/**
 * Build a v3 `LockFile` from the resolver's output.
 *
 * Assets are keyed by canonical ref `<kind>:@<publisher>/<name>#<version>`; the
 * top-level `sources:` block lists every contributing source URL pinned to its
 * resolved commit.
 *
 * @param resolved - Resolved provenance candidates (the resolver's `resolved` array).
 * @param overridesApplied - Canonical refs that an `overrides[]` entry pinned.
 * @returns Complete v2 `LockFile` ready for serialization.
 * @docLink packages/core/api-reference#build-lock-file
 */
export function buildLockFile(
  resolved: ProvenanceCandidate[],
  overridesApplied: Set<string>,
): LockFile {
  const assets: Record<string, LockEntry> = {};
  const sourceMap = new Map<string, LockSourceEntry>();

  for (const c of resolved) {
    const canonicalRef = `${c.kind}:@${c.publisher}/${c.name}#${c.version}`;
    assets[canonicalRef] = {
      sha256: c.sha256,
      source: { url: c.sourceUrl, commit: c.commit },
      files: c.files,
      override_applied: overridesApplied.has(canonicalRef),
    };
    if (!sourceMap.has(c.sourceUrl)) {
      sourceMap.set(c.sourceUrl, { url: c.sourceUrl, commit: c.commit });
    }
  }

  return {
    schema_version: 3,
    locked_at: new Date().toISOString(),
    assets,
    sources: Array.from(sourceMap.values()).sort((a, b) => a.url.localeCompare(b.url)),
  };
}

// ── Plugins slice ────────────────────────────────────────────────────────────

/** Absolute path to a project's lock file. */
function lockPathFor(projectDir: string): string {
  return join(projectDir, "skaile.lock.yaml");
}

/**
 * Read the `plugins` slice from a project's `skaile.lock.yaml`.
 *
 * Total — never throws on a missing/unparseable/legacy lock file (returns
 * `undefined`). The plugin-store reconciler folds this into its reconcile-hash
 * so a lock edit invalidates the hash even without a manifest change.
 *
 * @param projectDir - Project root containing `skaile.lock.yaml`
 * @returns The `plugins` record, or `undefined` when absent
 */
export function readPluginsLockSlice(
  projectDir: string,
): Record<string, LockPluginEntry> | undefined {
  try {
    return readLock(lockPathFor(projectDir))?.plugins;
  } catch {
    // Legacy v1 lock — no usable plugins slice.
    return undefined;
  }
}

/**
 * Merge a `plugins` slice into a project's `skaile.lock.yaml`, preserving all
 * other fields. Creates a minimal v3 lock file when none exists (or the
 * existing one is an outdated schema that can't be read).
 *
 * @param projectDir - Project root containing `skaile.lock.yaml`
 * @param slice - The `plugins` record to persist
 */
export function writePluginsLockSlice(
  projectDir: string,
  slice: Record<string, LockPluginEntry>,
): void {
  const lockPath = lockPathFor(projectDir);
  let existing: LockFile | null = null;
  try {
    existing = readLock(lockPath);
  } catch {
    existing = null; // outdated schema — overwrite with a fresh v3 shell
  }
  const next: LockFile = existing ?? {
    schema_version: 3,
    locked_at: new Date().toISOString(),
    assets: {},
    sources: [],
  };
  next.plugins = slice;
  writeLock(lockPath, next);
}

// ── Verification ─────────────────────────────────────────────────────────────

/**
 * Result of verifying a lock file against the current repository state.
 * @docLink packages/core/api-reference#verify-result
 */
export interface VerifyResult {
  /** `true` when no drift or missing assets were found. */
  ok: boolean;
  /** Canonical refs whose content hash no longer matches. */
  drifted: string[];
  /** Canonical refs whose files cannot be found on disk. */
  missing: string[];
}

/**
 * Verify a v2 lock file against the current on-disk source clones.
 *
 * For each locked asset, resolve its source clone directory via `resolveCloneDir`
 * (URL → on-disk path), re-hash the recorded files, and compare the recomputed
 * composite against the locked `sha256`. Reports assets whose hash changed
 * (`drifted`) and assets whose files are missing (`missing`).
 *
 * @param lock - Previously generated v2 `LockFile`.
 * @param resolveCloneDir - Maps a source URL to its on-disk clone root (or null).
 * @returns `VerifyResult` indicating drift and missing assets.
 * @docLink packages/core/api-reference#verify-lock
 */
export function verifyLock(
  lock: LockFile,
  resolveCloneDir: (sourceUrl: string) => string | null,
): VerifyResult {
  const drifted: string[] = [];
  const missing: string[] = [];

  for (const [ref, entry] of Object.entries(lock.assets)) {
    const cloneDir = resolveCloneDir(entry.source.url);
    if (!cloneDir) {
      missing.push(ref);
      continue;
    }
    let allPresent = true;
    const recomputed: Array<{ path: string; sha256: string }> = [];
    for (const f of entry.files) {
      const abs = join(cloneDir, f.path);
      if (!existsSync(abs)) {
        allPresent = false;
        break;
      }
      recomputed.push({ path: f.path, sha256: computeHash(abs) });
    }
    if (!allPresent) {
      missing.push(ref);
      continue;
    }
    if (compositeHash(recomputed) !== entry.sha256) {
      drifted.push(ref);
    }
  }

  return { ok: drifted.length === 0 && missing.length === 0, drifted, missing };
}
